import React, { useEffect, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  Table,
  TabPane,
} from "reactstrap"
import { Link } from "react-router-dom"
import withRouter from "components/Common/withRouter"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { getProfileDetails } from "store/actions/authAction"
import Spinners from "components/Shared/Spinners"
import { QRCodeCanvas } from "qrcode.react"
import { UpdateGeneralDetails } from "./UpdateUserProfile/UpdateGeneralDetails"
import { getDropdownData } from "store/actions/commonAction"
import UpdateBankDetails from "./UpdateUserProfile/UpdateBankDetails"
import UpdateJobApplication from "./UpdateUserProfile/UpdateJobApplication"
import UploadDocument from "./UpdateUserProfile/UploadDocument"
import BackButton from "components/Shared/BackButton"
import UpdateAssetsDelivery from "./UpdateUserProfile/UpdateAssetsDelivery"
import Switch from "../../components/Shared/Switch"

const UserProfile = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Profile | Yamama")

  const dispatch = useDispatch()
  // State to manage active tab
  const [activeTab, setActiveTab] = useState("general")
  const { loading, isLoading, userDetails } = useSelector(state => state.Login)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalType, setModalType] = useState("GENERAL_DETAILS")
  const [idCardReceived, setIdCardReceived] = useState(false)
  const [uniformReceived, setUniformReceived] = useState(false)
  const toggleModal = () => setModalOpen(!modalOpen)
  const { dropdownData } = useSelector(state => state.Common)

  // Function to toggle tabs
  const toggleTab = tab => {
    if (activeTab !== tab) {
      setActiveTab(tab)
    }
  }

  useEffect(() => {
    getDetails()
  }, [])

  useEffect(() => {
    const fetchDropdownApi = () => {
      const payload = {
        type: "all",
      }
      dispatch(getDropdownData(payload))
    }
    fetchDropdownApi()
  }, [])

  useEffect(() => {
    if (userDetails?.user_information?.id_card_status === 3) {
      setIdCardReceived(true)
    }
    if (userDetails?.user_information?.uniform_status === 3) {
      setUniformReceived(true)
    }
  }, [userDetails])

  const getDetails = () => {
    const uuid = localStorage.getItem("uuid")
    dispatch(getProfileDetails(uuid))
  }

  const { userType } = useSelector(state => state.Login)

  const documentsObject =
    !loading && userDetails?.documents
      ? userDetails.documents.reduce((acc, doc) => {
          if (doc.file_type && doc.file_url) {
            acc[doc.file_type] = doc.file_url // Store the file_url for each file_type
          }
          return acc
        }, {})
      : {} // Ensure documentsObject is always an object

  const [uploadDocType, setUploadDocType] = useState(null)

  return (
    <React.Fragment>
      <div className="page-content pg_user_profile">
        {loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <Col lg="12">
                <div className="d-flex align-items-center mb-4">
                  <BackButton />

                  <h4 className="card-title mb-0">{t("User Profile")}</h4>
                </div>

                {userType === "candidate" && (
                  <>
                    {/* Tabs Navigation */}
                    <Nav tabs className="one_line_parent">
                      <NavItem>
                        <NavLink
                          className={activeTab === "general" ? "active" : ""}
                          onClick={() => toggleTab("general")}
                        >
                          {t("General Information")}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={activeTab === "bank" ? "active" : ""}
                          onClick={() => toggleTab("bank")}
                        >
                          {t("Bank Information")}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={activeTab === "job" ? "active" : ""}
                          onClick={() => toggleTab("job")}
                        >
                          {t("Job Information")}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={activeTab === "documents" ? "active" : ""}
                          onClick={() => toggleTab("documents")}
                        >
                          {t("Documents")}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={activeTab === "assets" ? "active" : ""}
                          onClick={() => toggleTab("assets")}
                        >
                          {t("Assets")}
                        </NavLink>
                      </NavItem>
                    </Nav>

                    {/* Tab Content */}
                    <TabContent activeTab={activeTab} className="py-3">
                      {/* General Information Tab */}
                      <TabPane tabId="general">
                        <Card className="theme_card">
                          <CardBody>
                            <div className="g_end">
                              <Button
                                color="success"
                                onClick={() => {
                                  setModalType("GENERAL_DETAILS")
                                  toggleModal()
                                }}
                              >
                                {t("Update General Details")}
                              </Button>
                            </div>
                            <Table className="table-wrap mb-0">
                              <tbody>
                                <tr>
                                  <th scope="row">{t("Id")}</th>
                                  <td>{userDetails?.user_information?.id}</td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("Profile Picture")}</th>
                                  <td>
                                    <div>
                                      <img
                                        src={
                                          userDetails?.user_information
                                            ?.profile_picture
                                        }
                                        alt=""
                                        style={{
                                          maxWidth: "100%",
                                          height: "auto",
                                          maxHeight: 200,
                                        }}
                                        className="img-fluid"
                                      />
                                    </div>
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">
                                    {t("First Name(English)")}
                                  </th>
                                  <td>
                                    {userDetails?.user_information?.first_name}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">
                                    {t("Middle Name(English)")}
                                  </th>
                                  <td>
                                    {userDetails?.user_information
                                      ?.middle_name || "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("Last Name(English)")}</th>
                                  <td>
                                    {userDetails?.user_information?.last_name}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("First Name(Arabic)")}</th>
                                  <td>
                                    {
                                      userDetails?.user_information
                                        ?.first_name_ar
                                    }
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">
                                    {t("Middle Name(Arabic)")}
                                  </th>
                                  <td>
                                    {userDetails?.user_information
                                      ?.middle_name_ar || "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("Last Name(Arabic)")}</th>
                                  <td>
                                    {
                                      userDetails?.user_information
                                        ?.last_name_ar
                                    }
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">
                                    {t("Suggested Job Title")}
                                  </th>
                                  <td>
                                    {userDetails?.user_information?.job_title}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("Email")}</th>
                                  <td>
                                    {" "}
                                    {userDetails?.user_information?.email}{" "}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("Phone Number")}</th>
                                  <td>
                                    <p className="ltr fit_content">
                                      {
                                        userDetails?.user_information
                                          ?.phone_code
                                      }{" "}
                                      {
                                        userDetails?.user_information
                                          ?.phone_number
                                      }
                                    </p>
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("National Id")}</th>
                                  <td>
                                    {userDetails?.user_information?.national_id}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("Registered Year")}</th>
                                  <td>
                                    {
                                      userDetails?.user_information
                                        ?.registered_year
                                    }
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">
                                    {t("National Id Expire Date")}
                                  </th>
                                  <td>
                                    <p className="ltr fit_content">
                                      {userDetails?.user_information
                                        ?.national_id_exp_date || "-"}
                                    </p>
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("Last Logged In At")}</th>
                                  <td>
                                    <p className="ltr fit_content">
                                      {
                                        userDetails?.user_information
                                          ?.last_logged_in_at
                                      }
                                    </p>
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("Nationality")}</th>
                                  <td>
                                    {" "}
                                    {
                                      userDetails?.job_application
                                        ?.nationality_name
                                    }
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("Date of Birth")}</th>
                                  <td> {userDetails?.user_information?.dob}</td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("Blood Group")}</th>
                                  <td>
                                    {" "}
                                    {userDetails?.user_information?.blood_group}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("Gender")}</th>
                                  <td>
                                    {" "}
                                    {t(userDetails?.user_information?.gender)}
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </CardBody>
                        </Card>
                      </TabPane>

                      {/* Bank Information Tab */}
                      <TabPane tabId="bank">
                        <Card className="theme_card">
                          <CardBody>
                            <div className="g_end">
                              <Button
                                color="success"
                                onClick={() => {
                                  setModalType("BANK_DETAILS")
                                  toggleModal()
                                }}
                              >
                                {t("Update Bank Details")}
                              </Button>
                            </div>
                            <Table className="table-wrap mb-0">
                              <tbody>
                                <tr>
                                  <th scope="row">{t("Bank Name")}</th>
                                  <td>
                                    {" "}
                                    {userDetails?.job_application?.bank_name}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("IBAN Number")}</th>
                                  <td>
                                    {userDetails?.job_application?.bank_iban}
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </CardBody>
                        </Card>
                      </TabPane>

                      {/* Job Information Tab */}
                      <TabPane tabId="job">
                        <Card className="theme_card">
                          <CardBody>
                            <div className="g_end">
                              <Button
                                color="success"
                                onClick={() => {
                                  setModalType("JOB_APPLICATION_DETAILS")
                                  toggleModal()
                                }}
                              >
                                {t("Update Job Application Details")}
                              </Button>
                            </div>
                            <Table className="table-wrap mb-0">
                              <tbody>
                                <tr>
                                  <th scope="row">
                                    {t("Educational Qualification")}
                                  </th>
                                  <td>
                                    {
                                      userDetails?.job_application
                                        ?.education_qualification_name
                                    }
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("Specialization")}</th>
                                  <td>
                                    {
                                      userDetails?.job_application
                                        ?.specialization_name
                                    }
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("City of Residence")}</th>
                                  <td>
                                    {userDetails?.job_application?.city_name}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("Language")}</th>
                                  <td>
                                    {userDetails?.job_application?.languages?.map(
                                      item => item.name + ", "
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("Uniform Size")}</th>
                                  <td>
                                    {
                                      userDetails?.job_application
                                        ?.uniform_size_name
                                    }
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">
                                    {t(
                                      "Do you have chronic illnesses or health issues?"
                                    )}
                                  </th>
                                  <td>
                                    {userDetails?.job_application
                                      ?.has_health_issues
                                      ? t("Yes")
                                      : t("No")}
                                  </td>
                                </tr>
                                {userDetails?.job_application
                                  ?.has_health_issues && (
                                  <tr>
                                    <th scope="row">{t("Health Issues")}</th>
                                    <td>
                                      {
                                        userDetails?.job_application
                                          ?.health_issues
                                      }
                                    </td>
                                  </tr>
                                )}
                                <tr>
                                  <th scope="row">
                                    {t(
                                      "Have you ever worked in crowd management at the Holy Train of the Sacred Sites?"
                                    )}
                                  </th>
                                  <td>
                                    {userDetails?.job_application
                                      ?.has_crowd_management_exp
                                      ? t("Yes")
                                      : t("No")}
                                  </td>
                                </tr>
                                {userDetails?.job_application
                                  ?.has_crowd_management_exp && (
                                  <tr>
                                    <th scope="row">
                                      {t("Crowd Management Experience")}
                                    </th>
                                    <td>
                                      {userDetails?.job_application?.crowd_management_exp?.map(
                                        item => (
                                          <div
                                            key={item?.job}
                                            className="d-flex align-items-center justify-content-between my-2  pe-3"
                                          >
                                            <h6>{item?.job_title}</h6>
                                            <h6 className="font-bold">
                                              {item?.station_title}
                                            </h6>
                                          </div>
                                        )
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>
                          </CardBody>
                        </Card>
                      </TabPane>

                      {/* Documents Tab */}
                      <TabPane tabId="documents">
                        <Card className="theme_card">
                          <CardBody>
                            <Table className="table-wrap mb-0">
                              <tbody>
                                <tr>
                                  <th scope="row">{t("Personal Photo")}</th>
                                  <td>
                                    <Link
                                      to={
                                        userDetails?.user_information
                                          ?.profile_picture || "#"
                                      }
                                      className="text-decoration-underline"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {t("View")}
                                    </Link>

                                    <Button
                                      color="success"
                                      className="mx-4"
                                      onClick={() => {
                                        setUploadDocType(
                                          "personal_profile_image"
                                        )
                                        setModalType("DOCUMENT")
                                        toggleModal()
                                      }}
                                    >
                                      {t("Update")}
                                    </Button>
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("ID/Residence Image")}</th>
                                  <td>
                                    {documentsObject["National_ID"] ? (
                                      <Link
                                        to={
                                          documentsObject["National_ID"] || "#"
                                        }
                                        className="text-decoration-underline"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {t("View")}
                                      </Link>
                                    ) : (
                                      <Button
                                        color="success"
                                        onClick={() => {
                                          setUploadDocType("national_id_image")
                                          setModalType("DOCUMENT")
                                          toggleModal()
                                        }}
                                      >
                                        {t("Upload")}
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("CV")}</th>
                                  <td>
                                    {documentsObject["CV"] ? (
                                      <Link
                                        to={documentsObject["CV"] || "#"}
                                        className="text-decoration-underline"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {t("View")}
                                      </Link>
                                    ) : (
                                      <Button
                                        color="success"
                                        onClick={() => {
                                          setUploadDocType("cv_attachment")
                                          setModalType("DOCUMENT")
                                          toggleModal()
                                        }}
                                      >
                                        {t("Upload")}
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">
                                    {t("Supporting Document")}
                                  </th>
                                  <td>
                                    {documentsObject["Supporting_Document"] ? (
                                      <Link
                                        to={
                                          documentsObject[
                                            "Supporting_Document"
                                          ] || "#"
                                        }
                                        className="text-decoration-underline"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {t("View")}
                                      </Link>
                                    ) : (
                                      <Button
                                        color="success"
                                        onClick={() => {
                                          setUploadDocType(
                                            "supporting_document"
                                          )
                                          setModalType("DOCUMENT")
                                          toggleModal()
                                        }}
                                      >
                                        {t("Upload")}
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </CardBody>
                        </Card>
                      </TabPane>

                      {/* Assets Management Tab */}
                      <TabPane tabId="assets">
                        <Card className="theme_card">
                          <CardBody>
                            <Table className="table-wrap mb-0">
                              <thead>
                                <tr>
                                  <th>{t("Assets")}</th>
                                  <th>{t("Details")}</th>
                                  <th>{t("Status")}</th>
                                  <th>{t("Received")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <th scope="row">{t("ID Card")}</th>
                                  <td>
                                    {" "}
                                    {userDetails?.user_information
                                      ?.id_card_serial_number ||
                                      t("Not yet generated")}
                                  </td>
                                  <td>
                                    {userDetails?.user_information
                                      ?.id_card_status === 0
                                      ? t("Generated")
                                      : userDetails?.user_information
                                          ?.id_card_status === 1
                                      ? t("Printed")
                                      : userDetails?.user_information
                                          ?.id_card_status === 2
                                      ? t("Dispatched")
                                      : userDetails?.user_information
                                          ?.id_card_status === 3
                                      ? t("Confirmed")
                                      : t("Pending")}
                                  </td>
                                  <td>
                                    <Switch
                                      onValue={t("")}
                                      offValue={t("")}
                                      isChecked={idCardReceived}
                                      onClick={() =>
                                        setIdCardReceived(!idCardReceived)
                                      }
                                      onClickHandler={e => console.log(e)}
                                      onChange={e => {
                                        setIdCardReceived(e.target.checked)
                                      }}
                                      disabled={
                                        userDetails?.user_information
                                          ?.id_card_status !== 2
                                      }
                                    />
                                  </td>
                                </tr>
                                <tr>
                                  <th scope="row">{t("Uniform")}</th>
                                  <td>
                                    {
                                      userDetails?.job_application
                                        ?.uniform_size_name
                                    }
                                  </td>
                                  <td>
                                    {userDetails?.user_information
                                      ?.uniform_status === 0
                                      ? t("Generated")
                                      : userDetails?.user_information
                                          ?.uniform_status === 1
                                      ? t("Printed")
                                      : userDetails?.user_information
                                          ?.uniform_status === 2
                                      ? t("Dispatched")
                                      : userDetails?.user_information
                                          ?.uniform_status === 3
                                      ? t("Confirmed")
                                      : t("Pending")}
                                  </td>
                                  <td>
                                    <Switch
                                      onValue={t("")}
                                      offValue={t("")}
                                      isChecked={uniformReceived}
                                      onClick={() =>
                                        setUniformReceived(!uniformReceived)
                                      }
                                      onClickHandler={e => console.log(e)}
                                      onChange={e => {
                                        setUniformReceived(e.target.checked)
                                      }}
                                      disabled={
                                        userDetails?.user_information
                                          ?.uniform_status !== 2
                                      }
                                    />
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                            {(userDetails?.user_information?.id_card_status ===
                              2 ||
                              userDetails?.user_information?.uniform_status ===
                                2) && (
                              <div className="g_end mt-3">
                                <Button
                                  color="success"
                                  onClick={() => {
                                    setModalType("ASSETS_DELIVERY_CONFIRMATION")
                                    toggleModal()
                                  }}
                                >
                                  {t("Save")}
                                </Button>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </TabPane>
                    </TabContent>

                    {userDetails?.user_information?.national_id && (
                      <Card>
                        <CardBody>
                          <CardTitle>{t("QR Code")}</CardTitle>
                          <div className="mt-4">
                            <QRCodeCanvas
                              value={userDetails?.user_information?.national_id}
                              size={300}
                            />
                          </div>
                        </CardBody>
                      </Card>
                    )}
                  </>
                )}

                {(userType === "superadmin" || userType === "staff") && (
                  <Card className="theme_card">
                    <CardBody>
                      <Table className="table-wrap mb-0">
                        <tbody>
                          <tr>
                            <th scope="row">{t("Id")}</th>
                            <td>{userDetails?.user_information?.id}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("First Name(English)")}</th>
                            <td>{userDetails?.user_information?.first_name}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Middle Name(English)")}</th>
                            <td>
                              {userDetails?.user_information?.middle_name}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Last Name(English)")}</th>
                            <td> {userDetails?.user_information?.last_name}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("First Name(Arabic)")}</th>
                            <td>
                              {userDetails?.user_information?.first_name_ar}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Middle Name(Arabic)")}</th>
                            <td>
                              {userDetails?.user_information?.middle_name_ar ||
                                "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Last Name(Arabic)")}</th>
                            <td>
                              {userDetails?.user_information?.last_name_ar}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Phone Number")}</th>
                            <td>
                              <p className="ltr fit_content">
                                {" "}
                                {userDetails?.user_information?.phone_code}{" "}
                                {userDetails?.user_information?.phone_number}{" "}
                              </p>
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Email")}</th>
                            <td> {userDetails?.user_information?.email} </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("National Id")}</th>
                            <td>
                              {userDetails?.user_information?.national_id}{" "}
                            </td>
                          </tr>
                          {/* <tr>
                            <th scope="row">{t("National Id Expire Date")}</th>
                            <td>{userDetails?.user_information?.dob}</td>
                          </tr> */}
                          {/* <tr>
                            <th scope="row">{t("Nationality")}</th>
                            <td>Saudi</td>
                          </tr> */}
                          {/* <tr>
                            <th scope="row">{t("Gender")}</th>
                            <td className="text-capitalize">
                              {t(userDetails?.user_information?.gender)}
                            </td>
                          </tr> */}
                          <tr>
                            <th scope="row">{t("Last Logged In At")}</th>
                            <td>
                              <p className="ltr fit_content">
                                {
                                  userDetails?.user_information
                                    ?.last_logged_in_at
                                }
                              </p>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                )}
              </Col>
            </Row>
          </Container>
        )}
      </div>
      <Modal
        isOpen={modalOpen}
        toggle={toggleModal}
        size="lg"
        backdrop="static"
      >
        <ModalHeader toggle={toggleModal}>
          {modalType === "GENERAL_DETAILS"
            ? t("Update General Details")
            : modalType === "BANK_DETAILS"
            ? t("Update Bank Details")
            : modalType === "JOB_APPLICATION_DETAILS"
            ? t("Update Job Application Details")
            : modalType === "DOCUMENT"
            ? t("Upload Document")
            : modalType === "ASSETS_DELIVERY_CONFIRMATION"
            ? t("Are you sure you want to save this response?")
            : ""}
        </ModalHeader>
        <ModalBody>
          {modalType === "GENERAL_DETAILS" ? (
            <UpdateGeneralDetails
              dropdownData={dropdownData}
              userDetails={userDetails}
              toggleModal={toggleModal}
              getDetails={getDetails}
            />
          ) : modalType === "BANK_DETAILS" ? (
            <UpdateBankDetails
              dropdownData={dropdownData}
              userDetails={userDetails}
              toggleModal={toggleModal}
              getDetails={getDetails}
            />
          ) : modalType === "JOB_APPLICATION_DETAILS" ? (
            <UpdateJobApplication
              dropdownData={dropdownData}
              userDetails={userDetails}
              toggleModal={toggleModal}
              getDetails={getDetails}
            />
          ) : modalType === "DOCUMENT" ? (
            <UploadDocument
              uploadDocType={uploadDocType}
              toggleModal={toggleModal}
              getDetails={getDetails}
              userDetails={userDetails}
            />
          ) : modalType === "ASSETS_DELIVERY_CONFIRMATION" ? (
            <UpdateAssetsDelivery
              toggleModal={toggleModal}
              getDetails={getDetails}
              userDetails={userDetails}
              idCardReceived={idCardReceived}
              uniformReceived={uniformReceived}
            />
          ) : (
            ""
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

export default withRouter(UserProfile)
